/**
 * Our wrapper for day.js
 * @rmonge: Since we moved from moment.js to day.js I'm creating this
 * wrapper file arround day.js in case in the future we move to other library.
 * In that case the migration will be less painful.
 */
import dayjs from "dayjs";
import customParseFormat from 'dayjs/plugin/customParseFormat';
import localeData from 'dayjs/plugin/localeData';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isBetween from 'dayjs/plugin/isBetween';
import advancedFormat from 'dayjs/plugin/advancedFormat';

/**
 * The site language
 */
const siteLang = document.documentElement.dataset.lang || document.documentElement.lang;

/**
 * We include all languages that are supported by the CMS
 * If we import them dynamicaly dayjs is adding all the languages (webpack issues).
 * So, this is a uglybut its working.
 * See this link: https://github.com/iamkun/dayjs/issues/792
 */
require(`dayjs/locale/ar`);
require(`dayjs/locale/bg`);
require(`dayjs/locale/ca`);
require(`dayjs/locale/cs`);
require(`dayjs/locale/da`);
require(`dayjs/locale/de`);
require(`dayjs/locale/el`);
require(`dayjs/locale/en`);
require(`dayjs/locale/en-gb`);
require(`dayjs/locale/en-sg`);
require(`dayjs/locale/es`);
require(`dayjs/locale/et`);
require(`dayjs/locale/fa`);
require(`dayjs/locale/fi`);
require(`dayjs/locale/fr`);
require(`dayjs/locale/he`);
require(`dayjs/locale/hr`);
require(`dayjs/locale/hu`);
require(`dayjs/locale/id`);
require(`dayjs/locale/is`);
require(`dayjs/locale/it`);
require(`dayjs/locale/ja`);
require(`dayjs/locale/ko`);
require(`dayjs/locale/lt`);
require(`dayjs/locale/lv`);
require(`dayjs/locale/me`);
require(`dayjs/locale/ms`);
require(`dayjs/locale/my`);
require(`dayjs/locale/nb`);
require(`dayjs/locale/nl`);
require(`dayjs/locale/pl`);
require(`dayjs/locale/pt`);
require(`dayjs/locale/pt-br`);
require(`dayjs/locale/ro`);
require(`dayjs/locale/ru`);
require(`dayjs/locale/sk`);
require(`dayjs/locale/sl`);
require(`dayjs/locale/sr`);
require(`dayjs/locale/sv`);
require(`dayjs/locale/th`);
require(`dayjs/locale/tr`);
require(`dayjs/locale/uk`);
require(`dayjs/locale/vi`);
require(`dayjs/locale/zh-cn`);
require(`dayjs/locale/zh-tw`);

/**
 * DateTime Class. 
 * It is a wrapper for the dayjs library.
 */
class DateTime {
  /**
   * Creates the instance of the classe
   * @param {String} date 
   * @param {String} format 
   * @param {String} locale 
   */
  constructor(date, format, locale) {
    dayjs.extend(customParseFormat);
    dayjs.extend(localeData);
    dayjs.extend(isSameOrBefore);
    dayjs.extend(isBetween);
    dayjs.extend(advancedFormat);

    this.locale(siteLang);
    this.instance = dayjs(date, format, locale);
    this.instanceLocaleData = dayjs().localeData();
  }

  /**
   * Changes the locale of the object.
   * Changing the global locale doesn't affect existing instances.
   * @param {String} lang 
   */
  locale(lang) {
    try {
      let language;
      switch (lang) {
        case 'br':
          language = 'pt-br';
          break;
        case 'gb':
          language = 'en-gb';
          break;
        case 'sg':
          language = 'en-sg';
          break;
        case 'zh':
          language = 'zh-cn';
          break;
        case 'zt':
          language = 'zh-tw';
          break;
        default:
          language = lang;
          break;
      }
      dayjs.locale(language);
    } catch (e) {
      console.error(`[datetime] trying to load language ${lang}`, e);
    }
  }

  /**
   * LocaleData extends dayjs().localeData API to supply locale data.
   * @returns 
   */
  localeData() {
    return dayjs().localeData();
  }

  /**
   * Returns the days of the week
   * @returns array
   */
  weekdays() {
    return this.instanceLocaleData.weekdays();
  }

  /**
   * Returns the days of the week short name
   * @returns array
   */
  weekdaysShort() {
    return this.instanceLocaleData.weekdaysShort();
  }

  /**
   * Returns the month name list
   * @returns array
   */
  months() {
    return this.instanceLocaleData.months();
  }

  /**
   * Returns the month abbreviated name list
   * @returns array
   */
  monthsShort() {
    return this.instanceLocaleData.monthsShort();
  }

  /**
   * Returns the date formated by format
   * @param {String} format 
   * @returns String
   */
  format(format) {
    return this.instance.format(format);
  }

  /**
   * Returns the difference between dates in measurement unit.
   * @param {DateTime} datetime 
   * @param {String} measurement 
   * @param {Boolean} withDecimals 
   * @returns 
   */
  diff(datetime, measurement, withDecimals) {
    return this.instance.diff(datetime.instance, measurement, withDecimals);
  }

  /**
   * Gets or sets the year
   */
  year(v) {
    return this.instance.year(v);
  }

  /**
   * Gets or sets the month
   */
  month(v) {
    return this.instance.month(v);
  }

  /**
   * Returns the month name of a DateTime
   */
  monthName() {
    return this.instance.format('MM');
  }

  /**
   * Returns the month short name of a DateTime
   */
  monthShortName() {
    return this.instance.format('MMM');
  }

  /**
   * Returns the month full name of a DateTime
   */
  monthFullName() {
    return this.instance.format('MMMM');
  }

  /**
   * Gets or sets the day of the month.
   * @param {Integer} v 
   * @returns 
   */
  date(v) {
    return this.instance.date(v);
  }

  /**
   * Returns the end of a date based on measurement
   * Ex> let d = datetime('12/12/1980', 'DD/MM/YYYY')
   *  d.endof('month') ---> 31/12/1980
   * @param {String} measurement 
   * @returns 
   */
  endOf(measurement) {
    return this.instance.endOf(measurement);
  }

  /**
   * Returns the start of a date based on measurement
   * Ex> let d = datetime('12/12/1980', 'DD/MM/YYYY')
   *  d.endof('month') ---> 01/12/1980
   * @param {String} measurement 
   * @returns 
   */
  startOf(measurement) {
    return this.instance.startOf(measurement);
  }

  /**
   * This returns a boolean indicating whether the 
   * Day.js object contains a valid date or not.
   * @returns Boolean
   */
  isValid() {
    return this.instance.isValid();
  }

  /**
   * This indicates whether a variable is a DateTime object or not.
   * @param {DateTime} datetime 
   * @returns Bool
   */
  isDateTime(datetime) {
    return dayjs.isDayjs(datetime.instance);
  }

  /**
   * This indicates whether the Day.js object is after the other supplied date-time.
   * @param {DateTime} datetime 
   * @param {String} measurement 
   * @returns Boolean
   */
  isAfter(datetime, measurement) {
    return this.instance.isAfter(datetime.instance, measurement);
  }

  /**
   * This indicates whether the Day.js object is before the other supplied date-time.
   * @param {DateTime} datetime 
   * @param {String} measurement 
   * @returns Boolean
   */
  isBefore(datetime, measurement) {
    return this.instance.isBefore(datetime.instance, measurement);
  }

  /**
  * Returns bolean indicating if a date is between two other dates.
  * @param {DateTime} datetimeA 
  * @param {DateTime} datetimeB
  * @param {String} measurement 
  * @returns Boolean
  */
  isBetween(datetimeA, datetimeB, measurement) {
    return this.instance.isBetween(datetimeA.instance, datetimeB.instance, measurement);
  }

  /**
   * Returns a boolean indicating if a date is same or before another date.
   * @param {DateTime} datetime 
   * @returns 
   */
  isSameOrBefore(datetime) {
    return this.instance.isSameOrBefore(datetime.instance);
  }

  /**
   * Returns a boolean indicating if a date is same as the other date.
   * @param {DateTime} datetime 
   * @returns 
   */
  isSame(datetime) {
    return this.instance.isSame(datetime.instance);
  }

  /**
   * Returns DateTime object with a specified amount of time added.
   * @param {Integer} value 
   * @param {String} measurement 
   * @returns 
   */
  add(value, measurement) {
    this.instance = this.instance.add(value, measurement);
    return this;
  }

  /**
   * Copy of the native Date object parsed from the Day.js object
   * @returns date string 
   */
  toString() {
    return this.instance.toDate();
  }

  /**
   * Copy of the native Date object parsed from the Day.js object
   * @returns date string 
   */
  toDate() {
    return this.instance.toDate();
  }

  /**
   * returns the number of milliseconds since the Unix Epoch of the Day.js object.
   * @returns milliseconds
   */
  valueOf() {
    return this.instance.valueOf();
  }

}

export const datetime = (date, c) => {
  return new DateTime(date, c);
}